import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

export type Direction = 'fwd' | 'back';

/** What the 次は… card announces while the wash is crossing. */
export interface NextCard {
  kanji: string;
  en: string;
}

export type TransitPhase = 'idle' | 'departing' | 'arriving';

/**
 * The ride between two views.
 *
 * In the concept each station was its own document and the animation was split
 * across a page load: depart, let the wash cover the screen, change
 * `location.href`, then have the next document replay the second half on load.
 * Here both halves happen in one document, so the swap is a state change timed
 * to land while the wash is fully covering — the same 740 ms, chosen so the
 * screen is solid at the moment the view underneath changes.
 */
export function useTransit<V>(initial: V) {
  const reduced = usePrefersReducedMotion();
  const [view, setView] = useState<V>(initial);
  const [phase, setPhase] = useState<TransitPhase>('idle');
  const [direction, setDirection] = useState<Direction>('fwd');
  const [card, setCard] = useState<NextCard | null>(null);

  const timers = useRef<number[]>([]);
  const clearTimers = () => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const travel = useCallback(
    (to: V, dir: Direction, announce: NextCard | null) => {
      clearTimers();
      if (reduced) {
        // No wash to hide the change behind, so there is nothing to time.
        setCard(null);
        setPhase('idle');
        setView(to);
        return;
      }
      setCard(announce);
      setDirection(dir);
      setPhase('departing');
      timers.current.push(
        window.setTimeout(() => {
          setView(to);
          setPhase('arriving');
          window.scrollTo(0, 0);
          // The card fades out over the arrival, so it keeps its words until then.
          timers.current.push(
            window.setTimeout(() => {
              setPhase('idle');
              setCard(null);
            }, 1200),
          );
        }, 740),
      );
    },
    [reduced],
  );

  /** Jump with no animation at all — used when results arrive and land the rider at the hub. */
  const jump = useCallback((to: V) => {
    clearTimers();
    setCard(null);
    setPhase('idle');
    setView(to);
  }, []);

  const classes = [
    phase === 'departing' ? 'departing' : '',
    phase === 'arriving' ? 'arriving' : '',
    phase === 'idle' ? '' : direction === 'fwd' ? 'go-fwd' : 'go-back',
  ]
    .filter(Boolean)
    .join(' ');

  return { view, phase, direction, card, classes, travel, jump, reduced };
}
